import ProviderInvoiceMessageFormConfig from "./form";
import GenericForm from "../GenericForm";
import { ProviderInvoiceApi, ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import { useEffect, useRef, useState } from "react";
import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import LoadXmlDataForm from "@components/dynamic/LoadXmlDataForm";
import { generateATVDocumentKey, generateATVDocumentSequence, generateATVDocumentSequenceNumber, loadXmlDocument, XmlSummaryData } from "@utils/EDocument";
import LoadingScreen from "@components/content/LoadingScreen";
import { ProviderInvoiceMessageFormData } from "@/formTypes/ProviderInvoiceMessageFormDataType";
import { Button, Card, CardText, Col, Label, Row } from "reactstrap";
import { ATV_RESPONSES_CODE_TO_EDOCUMET_TYPE, PROVIDER_INVOICE_CASH_TYPES, SITUACION_COMPROBANTE_NORMAL } from "@utils/Constants";
import { getCurrentDateTime } from "@utils/Api";
import { useAppSelector } from "@/store/hooks";
import { ApiError, NotFoundError } from "@/api/ApiClient";
import { EasyposEInvoice } from "@/atv/submitInvoice";
import { MessageMetadata } from "@/atv/eInvoiceApi";
import { DocumentSequenceApi } from "@/api/DocumentSequenceApi";
import { AtvMessageApi, AtvMessageDataType } from "@/api/AtvMessageApi";
import { showAlert } from "@utils/Alert";
import { ActivityCodeApi } from "@/api/ActivityCodeApi";
import { loadReferenceFormLabel } from "@utils/Forms";
import { markRecieveInvoiceAsValidated } from "@utils/RecieveEmailUtils";
import { ReceivedInvoiceMessageData } from "@/formTypes/ReceivedInvoiceDataType";

type ProviderInvoiceMessageFormProps = {
  formId: string,
  saveRedirectUrl: string
}

const ProviderInvoiceMessageForm = (props: ProviderInvoiceMessageFormProps) => {
  const formRef = useRef<DynamicFormRef>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [invoiceLoaded, setInvoiceLoaded] = useState<boolean>(false);
  const [providerInvoiceData, setProviderInvoiceData] = useState<ProviderInvoiceDataType>();
  const [documentSummary, setDocumentSummary] = useState<XmlSummaryData>({} as XmlSummaryData);
  const suscriptionData = useAppSelector(state => state.suscription.suscription);
  const [formConfig, setFormConfig] = useState<any>();
  const [receivedEmailId, setReceivedEmailId] = useState<string>();

  useEffect(() => {
    loadFormConfig()
  }, [suscriptionData])

  const loadFormConfig = () => {
    const formConfig = loadReferenceFormLabel(ProviderInvoiceMessageFormConfig, suscriptionData);
    if (suscriptionData?.id) {
      setFormConfig(formConfig)
    }
  }

  const onXMLFileLoad = async (fileData: any, receivedEmailId?: string) => {
    setIsLoading(true)
    try {
      if (receivedEmailId) {
        setReceivedEmailId(receivedEmailId);
      }
      const { messageData, providerInvoiceData, documentSummary } = await loadXmlDocument(fileData, ["FacturaElectronica"])
      setProviderInvoiceData(providerInvoiceData)
      setInvoiceLoaded(true)
      setDocumentSummary(documentSummary as XmlSummaryData)

      const defaultActivityCodeCurrency = await ActivityCodeApi.getDefaultActivityCode()

      formRef.current?.resetFormValue({
        ...messageData,
        activitycode: messageData?.activitycode || defaultActivityCodeCurrency?.code
      });
      const alreadyAdded = await documentExist(`${documentSummary?.documentKey}`)
      if (alreadyAdded) {
        showAlert("warning", "Esta factura ya se encuentra registrada en el sistema, al enviar este mensaje se volvera a actualizara la informacion del documento")
      }


      if (!providerInvoiceData?.providerId) {
        showAlert("warning", `El proveedor de este documento no se encuentra registrado en el sistema, la factura no sera automaticamente agregada a las cuentas por pagar`, {}, 7000)
      }
    } catch (err) {
      if (err instanceof Error) {
        showAlert("danger", err.message)
      }
    }
    finally {
      setIsLoading(false)
    }
  }

  const documentExist = async (documentKey: string) => {
    try {
      await ProviderInvoiceApi.getInvoiceByDocumentKey(documentKey);
    } catch (err) {
      if (err instanceof NotFoundError) {
        return false
      } else {
        throw err
      }
    }
    return true
  }

  const saveProviderInvoice = async (providerInvoiceData: ProviderInvoiceDataType): Promise<ProviderInvoiceDataType> => {
    let providerInvoice;

    try {
      providerInvoice = await ProviderInvoiceApi.getInvoiceByDocumentKey(`${providerInvoiceData.document_key}`);
    } catch (err) {
      if (err instanceof NotFoundError) {
        //invoices not found create it
        providerInvoice = await ProviderInvoiceApi.create(providerInvoiceData);
      } else {
        throw err
      }
    }
    return providerInvoice
  }

  const validateInvoice = async (data: ProviderInvoiceMessageFormData): Promise<ProviderInvoiceMessageFormData> => {
    let isExisteProvider = false
    if (suscriptionData && providerInvoiceData) {
      const sequence = await generateATVDocumentSequence(ATV_RESPONSES_CODE_TO_EDOCUMET_TYPE[`${data.mensaje}`])
      const sequenceNumber = generateATVDocumentSequenceNumber(sequence);
      const documentKey = generateATVDocumentKey({
        fecha: getCurrentDateTime(),
        sequence: sequenceNumber
      }, suscriptionData, SITUACION_COMPROBANTE_NORMAL, "fecha")

      let providerInvoice;

      if (data.providerId) {
        isExisteProvider = true;
        const isPagada = `${data.tipo}`.padStart(2, "0") == PROVIDER_INVOICE_CASH_TYPES
        const status = isPagada ? "pagada" : "pendiente"

        providerInvoice = await saveProviderInvoice(
          {
            ...providerInvoiceData,
            providerId: data.providerId,
            reference_number: data.reference_number,
            tipo: `${data.tipo}`.padStart(2, "0"),
            pagada: isPagada,
            status,
            providerInvoiceCategoryId: data.providerInvoiceCategoryId
          });
      }

      const atvMessage: AtvMessageDataType = {
        documentKey: documentKey,
        sequence: `${sequenceNumber}`,
        numeroCedulaEmisor: `${documentSummary.emisor?.Identificacion?.Numero}`,
        fechaEmisionDoc: `${documentSummary.fechaEmision}`,
        mensaje: `${data.mensaje}`,
        detalleMensaje: data.detalle || "",
        montoTotalImpuesto: Number(data.impuestoAcreditar || 0),
        codigoActividad: `${data.activitycode}`,
        condicionImpuesto: `${data.condicionImpuesto}`,
        montoTotalImpuestoAcreditar: Number(data.impuestoAcreditar || 0),
        montoTotalDeGastoAplicable: Number(data.gastoAplicable || 0),
        totalFactura: Number(documentSummary.totalComprobanteAmount || 0),
        providerInvoiceId: providerInvoice?.id,
        providerId: data.providerId || "none"
      }

      const savedMessage = await AtvMessageApi.create(atvMessage);

      const messageBody: Partial<MessageMetadata> = {
        ...atvMessage,
        id: savedMessage.id,
        clave: `${providerInvoiceData.document_key}`,
        numeroCedulaReceptor: `${documentSummary.receptor?.Identificacion?.Numero}`,
        numeroConsecutivoReceptor: sequenceNumber,
        tipo_identificacion: `${documentSummary.tipo_identificacion}`,
        identificacion: `${documentSummary.identificacion}`.padStart(2, '0'),
        metadata: {
          calltime: Date.now(),
          numeroConsecutivo: sequenceNumber,
          documentType: "Message",
          clave: documentKey,
          fechaEmisionUTC: `${documentSummary.fechaEmision}`
        }
      }

      const easyposEInvoice = new EasyposEInvoice(suscriptionData)
      await easyposEInvoice.submitMessage(messageBody);
      await DocumentSequenceApi.save(sequence)
      if (!isExisteProvider) {
        showAlert("warning", `El mensaje a Tributacion fue enviado correctamente, pero el proveedor de este documento no se encuentra 
          registrado en el sistema, por lo que la factura no fue agregada a cuentas por pagar. Por favor agregue la factura manualmente`, {}, 7000)
      }
      if (providerInvoice) {
        showAlert("success", `El mensaje a tributacion fue enviado correctamente y la factura #${providerInvoice.sequence} ha sido agrega a las cuentas por pagar`, {}, 7000)
        if (receivedEmailId) {
          const documentData: Partial<ReceivedInvoiceMessageData> = {
            documentId: providerInvoice.id,
            documentType: "providerNote",
            providerId: providerInvoice.providerId,
            reference_number: providerInvoice.reference_number,
          }
          setReceivedEmailId("")
          await markRecieveInvoiceAsValidated(receivedEmailId, messageBody, documentData)
        }
      }

      return data
    }
    throw new ApiError("No fue posible generar el consecutivo del Mensaje", "API_ERROR");
  }

  return (
    <>
      {formConfig?.sections && (<>
        <LoadXmlDataForm
          paramLoadable={true}
          onFileLoaded={onXMLFileLoad}
        />
        <GenericForm<ProviderInvoiceMessageFormData, ProviderInvoiceMessageFormData>
          ref={formRef}
          formId={props.formId}
          saveRedirectUrl={props.saveRedirectUrl}
          formConfig={formConfig}
          createFn={(...arg) => validateInvoice(...arg)}
          successMessage="La factura fue validada correctamente"
          errorMessage="No fue posible validar la factura correctamente"
        />
        {invoiceLoaded && (
          <>
            <Row className="g-1 mb-4">
              <Col md={{
                offset: 1,
                size: 9
              }}>
                <Card
                  className="mt-3"
                  body
                  color="light"
                >
                  <CardText>
                    <h3 className="text-center mb-3 mt-3">Emisor: {documentSummary.providerName}</h3>
                    <Row>
                      <Col md={{
                        offset: 2,
                        size: 3
                      }}>
                        <b >
                          Numero Factura:
                        </b>
                      </Col>
                      <Col md>
                        <Label>
                          {documentSummary.documentKey}
                        </Label>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={{
                        offset: 2,
                        size: 3
                      }}>
                        <b >
                          Total Impuestos:
                        </b>
                      </Col>
                      <Col md>
                        <Label>
                          {documentSummary.totalImpuesto}
                        </Label>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={{
                        offset: 2,
                        size: 3
                      }}>
                        <b >
                          Total Factura:
                        </b>
                      </Col>
                      <Col md>
                        <Label>
                          {documentSummary.totalComprobante}
                        </Label>
                      </Col>
                    </Row>
                  </CardText>
                </Card>
              </Col >
            </Row >
            <div className="d-flex justify-content-center">
              <Button color="primary" size="lg" type="submit" form={props.formId}>
                Enviar Mensaje
              </Button>
            </div>
          </>
        )}

        <LoadingScreen isLoading={isLoading} />
      </>
      )}
    </>
  )
}

export default ProviderInvoiceMessageForm;